import React, { useEffect, useMemo, useRef, useState } from 'react';

import { isCannedFieldElement, isDynamicElement, Select } from '@aline/common';
import { params } from '@aline/routes';
import {
  List,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from '@mui/material';
import { ListFilter } from 'lucide-react';
import { uniqBy } from 'ramda';
import { Editor } from 'slate';
import type { PanelVariableType, VariablePanelProps } from 'src/components';
import {
  AlineAlert,
  AlineAvatar,
  AlineAvatarGroup,
  AlineCheckbox,
  AlineTooltip,
  CannedFieldRow,
  SelectButton,
  useAreAnnotationsLoaded,
  useSetVariablePanelState,
  useStickyListStyles,
  useVariablePanelState,
  VariableForm,
  VariableRow,
  VariablesEmpty,
} from 'src/components';
import type { AvatarInfo } from 'src/components/AlineAvatar';
import { toAvatarPlaceholder } from 'src/components/AlineAvatar';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import type { SignatureFieldFormField } from 'src/editor/toolbars';
import {
  CannedFieldForm,
  getIsSignatureFieldFormField,
  SignatureFieldForm,
} from 'src/editor/toolbars';
import { useDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { Apollo } from 'src/gql/generated';
import { useGetCompareFieldsByDocPosition } from 'src/helpers';
import {
  useCannedFieldIdsInCurrentDoc,
  useCannedFieldsForDoc,
  useDocumentSignatureStatus,
  useGetCurrentDoc,
  useGetField,
  useIsAuthorizedToEditStorable,
} from 'src/hooks';
import { useSlateSelector } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

import type { VariableFilterItem } from './variable-panel-state-store';

export function VariablePanelBody(props: VariablePanelProps): JSX.Element {
  const { documentId } = props;
  const { value: editFieldId } = useDrawerSearchParam(params.rightPanel.editFieldId);

  useSyncFocusedVariableWithEditorSelection(props.variables);

  if (editFieldId) return <EditFieldPanel documentId={documentId} editFieldId={editFieldId} />;

  return <ExpandableVariableSections {...props} />;
}

function useSyncFocusedVariableWithEditorSelection(variables: PanelVariableType[]) {
  const setVariablePanelState = useSetVariablePanelState();
  const { document } = useGetCurrentDoc();
  const latestVersionTypename = document?.latestVersion.__typename;
  const focusedCannedNodeId = useSlateSelector<UUID | null>(editor => {
    const cannedEntry = Editor.above(editor, { match: isCannedFieldElement });
    return cannedEntry ? cannedEntry[0].cannedFieldId : null;
  });
  const focusedFieldId = useSlateSelector<UUID | null>(editor => {
    if (Editor.above(editor, { match: isCannedFieldElement })) return null;
    const fieldEntry = Editor.above(editor, { match: isDynamicElement });
    return fieldEntry ? fieldEntry[0].fieldId : null;
  });

  useEffect(() => {
    if (focusedCannedNodeId) {
      setVariablePanelState({
        focusedField: {
          __typename: 'CannedField',
          id: focusedCannedNodeId,
          nodeId: focusedCannedNodeId,
        },
      });
      return;
    }

    if (!focusedFieldId) {
      /**
       * A PDF has no Slate selection, so null is its steady state. Only treat
       * null as a deselection when the open document is actually Slate.
       */
      if (latestVersionTypename === 'DocumentVersion')
        setVariablePanelState({ focusedField: null });
      return;
    }

    const focusedVariable = variables.find(variable => variable.nodeId === focusedFieldId);
    setVariablePanelState({ focusedField: focusedVariable ?? null });
  }, [
    focusedCannedNodeId,
    focusedFieldId,
    latestVersionTypename,
    setVariablePanelState,
    variables,
  ]);
}

const useEditPanelStyles = makeStyles()(theme => ({
  container: {
    padding: theme.spacing(0, 2),
  },
  errorContainer: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    padding: theme.spacing(0, 2),
  },
}));

interface EditFieldPanelProps {
  documentId: UUID;
  editFieldId: UUID;
}
function EditFieldPanel({ documentId, editFieldId }: EditFieldPanelProps): JSX.Element {
  const { classes } = useEditPanelStyles();
  const { field, loading: fieldLoading } = useGetField({ variables: { id: editFieldId } });
  const { data: cannedData, loading: cannedLoading } = Apollo.useGetCannedFieldQuery({
    skip: fieldLoading || Boolean(field),
    variables: { id: editFieldId },
  });

  if (fieldLoading || (!field && cannedLoading && !cannedData?.cannedField))
    return (
      <div className={classes.container}>
        {Array.from({ length: 3 }).map((_, index) => {
          return <Skeleton key={index} height={64} />;
        })}
      </div>
    );

  if (!field && cannedData?.cannedField)
    return <CannedFieldForm cannedField={cannedData.cannedField} />;

  if (!field) return <div />;

  switch (field.__typename) {
    case 'SignatureField': {
      if (!getIsSignatureFieldFormField(field))
        return (
          <div className={classes.errorContainer}>
            <Typography align="center" variant="subtitle1">
              Invalid signature field. Contact support.
            </Typography>
          </div>
        );

      return <EditSignatureFieldPanel editSignature={field} />;
    }
    case 'DocumentVariable': {
      return <EditVariablePanel documentId={documentId} editVariable={field} />;
    }
  }
}

interface EditVariablePanelProps {
  documentId: UUID;
  editVariable: PanelVariableType;
}

function EditVariablePanel({ documentId, editVariable }: EditVariablePanelProps): JSX.Element {
  const isField = editVariable.isField;
  const { canEdit } = useIsAuthorizedToEditStorable(documentId);
  const { setValue: setEditFieldId } = useDrawerSearchParam(params.rightPanel.editFieldId);
  const { isSigned, wasSentForSignature } = useDocumentSignatureStatus(documentId);
  const isInOrAfterSigning = wasSentForSignature || isSigned;
  const isFieldInOrAfterSigning = isField && isInOrAfterSigning;
  const variableDisabledReason = isFieldInOrAfterSigning
    ? 'Cannot edit field during or after signing'
    : !canEdit
      ? 'Not authorized to edit document'
      : '';

  return (
    <VariableForm
      key={editVariable.id}
      disabledReason={variableDisabledReason}
      formDataCy={Select.document.editor.rightDrawer.editVariablePanel.container}
      variable={editVariable}
      onDone={() => setEditFieldId(null)}
    />
  );
}

interface EditSignaturePanelProps {
  editSignature: SignatureFieldFormField;
}

function EditSignatureFieldPanel({ editSignature }: EditSignaturePanelProps): JSX.Element {
  return (
    <SignatureFieldForm
      formDataCy={Select.document.editor.rightDrawer.editSignaturePanel.container}
      signatureField={editSignature}
    />
  );
}

function ExpandableVariableSections(props: VariablePanelProps): JSX.Element {
  const { classes } = useStickyListStyles();
  const { document } = useGetCurrentDoc();

  if (!document?.workflow)
    return (
      <List className={classes.listRoot} subheader={<li />}>
        <li>
          <ul className={classes.toggleList}>
            <VariableListHeader {...props} />
            <VariableList {...props} />
          </ul>
        </li>
      </List>
    );

  return (
    <List className={classes.listRoot} subheader={<li />}>
      <li>
        <ul className={classes.toggleList}>
          <VariableListHeader {...props} />
          <VariableList {...props} />
        </ul>
      </li>
    </List>
  );
}

function VariableList({ documentId, loading, variables }: VariablePanelProps): JSX.Element {
  const state = useVariablePanelState();
  const { classes } = useStickyListStyles();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const {
    cannedFields,
    hasError: cannedFieldsHaveError,
    loading: cannedFieldsLoading,
  } = useCannedFieldsForDoc(documentId);
  const { document } = useGetCurrentDoc(documentId);
  const { isSigned } = useDocumentSignatureStatus(documentId);
  const annotationsLoaded = useAreAnnotationsLoaded();
  /**
   * Only a PDF sources its canned placements from annotations, so only a PDF has to wait for
   * them. Gating on the runtime's `documentViewer` instead would never release on a Slate tab:
   * the editor instantiates a viewer for every tab, but nothing loads a document into it, so the
   * annotationsLoaded event never fires.
   */
  const cannedPlacementsLoading =
    document?.latestVersion.__typename === 'StaticFileVersion' && !annotationsLoaded && !isSigned;
  const cannedIdsInDoc = useCannedFieldIdsInCurrentDoc(tabId);
  const { sortFieldsByDocPosition } = useGetCompareFieldsByDocPosition(tabId);
  const filteredCannedFields = useMemo(() => {
    const inDoc = isSigned
      ? cannedFields
      : cannedFields.filter(field => cannedIdsInDoc.has(field.nodeId));
    if (!state.filterItems.length) return inDoc;
    const filterItemIds = new Set(state.filterItems.map(item => item.id));
    return inDoc.filter(f => {
      const assigneeUserId = f.assignee?.assignee?.id;
      return assigneeUserId && filterItemIds.has(assigneeUserId);
    });
  }, [cannedFields, cannedIdsInDoc, isSigned, state.filterItems]);
  const orderedItems = useMemo(() => {
    const { fields, nonFields } = variables.reduce<{
      fields: PanelVariableType[];
      nonFields: PanelVariableType[];
    }>(
      (acc, v) => {
        return v.isField
          ? { ...acc, fields: [...acc.fields, v] }
          : { ...acc, nonFields: [...acc.nonFields, v] };
      },
      { fields: [], nonFields: [] }
    );
    const filterItemIds = new Set(state.filterItems.map(item => item.id));
    const filteredFields = fields.filter(v => {
      if (!filterItemIds.size) return true;
      const varAssigneeId = getActionFilterItem(v.action)?.id ?? '';
      return filterItemIds.has(varAssigneeId);
    });
    const filteredNonFields = nonFields
      .filter(v => {
        if (!filterItemIds.size) return true;
        const varAssigneeId = getActionFilterItem(v.action)?.id ?? '';
        return filterItemIds.has(varAssigneeId);
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    const positionedItems: VariableListItem[] = [...filteredFields, ...filteredCannedFields].sort(
      sortFieldsByDocPosition
    );
    return [...positionedItems, ...filteredNonFields];
  }, [filteredCannedFields, sortFieldsByDocPosition, state.filterItems, variables]);

  if (loading || cannedFieldsLoading || cannedPlacementsLoading)
    return (
      <ul
        className={classes.collapseContainer}
        data-cy={Select.document.editor.rightDrawer.variable.list.container}
      >
        {Array.from({ length: 3 }).map((_, index) => {
          return (
            <li key={index} className={classes.row}>
              <Typography className={classes.inputLabel}>
                <Skeleton variant="text" width={64} />
              </Typography>
              <Skeleton height={32} variant="rectangular" width="100%" />
            </li>
          );
        })}
      </ul>
    );

  if (!cannedFieldsHaveError && !orderedItems.length)
    return <VariablesEmpty documentId={documentId} state={state} />;

  return (
    <ul
      className={classes.collapseContainer}
      data-cy={Select.document.editor.rightDrawer.variable.list.container}
    >
      {cannedFieldsHaveError && (
        <li className={classes.row}>
          <AlineAlert severity="error">
            Could not load auto-populated fields for this document.
          </AlineAlert>
        </li>
      )}
      {orderedItems.map(item => (
        <VariableListRow
          key={item.id}
          className={classes.row}
          documentId={documentId}
          item={item}
        />
      ))}
    </ul>
  );
}

type VariableListItem = PanelVariableType | Apollo.CannedFieldFullFragment;

function VariableListRow({
  className,
  documentId,
  item,
}: {
  className: string;
  documentId: UUID;
  item: VariableListItem;
}): JSX.Element {
  switch (item.__typename) {
    case 'CannedField':
      return <CannedFieldRow cannedField={item} className={className} documentId={documentId} />;
    default:
      return <VariableRow className={className} documentId={documentId} variable={item} />;
  }
}

function VariableListHeader(props: VariablePanelProps): JSX.Element {
  const { classes } = useStickyListStyles();

  return (
    <ListSubheader className={classes.stickyHeader}>
      <div className={classes.collapseButton}>
        <Typography noWrap className={classes.listSubheaderText} variant="body1">
          Variables
        </Typography>
        <FilterMenu {...props} />
      </div>
    </ListSubheader>
  );
}

const useFilterStyles = makeStyles()(theme => ({
  avatarGroup: {
    marginLeft: theme.spacing(1),
  },
  avatarText: {
    marginLeft: theme.spacing(1),
  },
  emptyText: {
    padding: theme.spacing(1),
  },
  filterButton: {
    marginLeft: theme.spacing(1),
  },
  menu: {
    minWidth: 210,
  },
}));

function FilterMenu({ variables }: VariablePanelProps): JSX.Element {
  const { classes } = useFilterStyles();
  const state = useVariablePanelState();
  const [open, setOpen] = useState<boolean>(false);
  const setVariablePanelState = useSetVariablePanelState();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const assigneesOrRoles = uniqBy(
    item => item.id,
    variables.flatMap(v => {
      const assigneeOrRole = getActionFilterItem(v.action);
      return assigneeOrRole ? [assigneeOrRole] : [];
    })
  );
  const filterItemIds = new Set(state.filterItems.map(item => item.id));

  return (
    <>
      <AlineTooltip arrow placement="top" title="Filter variables">
        <SelectButton
          ref={buttonRef}
          className={classes.filterButton}
          selected={open || Boolean(state.filterItems.length)}
          onClick={() => setOpen(!open)}
        >
          <ListFilter size={18} />
        </SelectButton>
      </AlineTooltip>
      <AlineAvatarGroup className={classes.avatarGroup} max={5}>
        {state.filterItems.map(userOrRole => {
          const title =
            userOrRole.__typename === 'User' ? userOrRole.displayName : userOrRole.roleName;

          return (
            <AlineTooltip key={userOrRole.id} arrow placement="top" title={title}>
              <AlineAvatar avatarInfo={getFilterItemAvatarInfo(userOrRole)} />
            </AlineTooltip>
          );
        })}
      </AlineAvatarGroup>
      <Menu
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        className={classes.menu}
        open={open}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setOpen(false)}
      >
        {!assigneesOrRoles.length && (
          <Typography className={classes.emptyText}>No assignees to filter</Typography>
        )}
        {assigneesOrRoles.map(assigneeOrRole => {
          const isInFilterItems = filterItemIds.has(assigneeOrRole.id);

          return (
            <MenuItem
              key={assigneeOrRole.id}
              dense
              onClick={() => {
                const newFilterItems = isInFilterItems
                  ? state.filterItems.filter(item => item.id !== assigneeOrRole.id)
                  : [...state.filterItems, assigneeOrRole];
                setVariablePanelState({ filterItems: newFilterItems });
              }}
            >
              <ListItemIcon>
                <AlineCheckbox checked={isInFilterItems} />
              </ListItemIcon>
              <AlineAvatar avatarInfo={getFilterItemAvatarInfo(assigneeOrRole)} />
              <Typography className={classes.avatarText}>
                {assigneeOrRole.__typename === 'User'
                  ? assigneeOrRole.displayName
                  : assigneeOrRole.roleName}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

function getActionFilterItem(action: PanelVariableType['action']): VariableFilterItem | null {
  if (action?.assignee) return action.assignee;
  if (!action?.assigneeRoleName) return null;
  return {
    __typename: 'RoleFilterItem',
    avatarColor: action.permission.avatarColor,
    id: action.permission.id,
    roleName: action.assigneeRoleName,
  };
}

function getFilterItemAvatarInfo(item: VariableFilterItem): AvatarInfo {
  return item.__typename === 'User' ? item : toAvatarPlaceholder(item.roleName, item.avatarColor);
}
